import fetcher from '@/services/fetcher'
import type { Benefit, ClaimedCode, Plan } from '@/types'
import {
  BenefitCodes,
  type TBenefitsCategoryValue,
  type TPlanTier,
} from '@/utils/helper-objects'
import { isPlan } from '@/utils/type-guard-utils'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { arrayWrap, isAvailableInPlan } from '@/utils/utils'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'

import { usePlansStore } from './plans'
import { useUserStore } from './user'

type BenefitsStoreState = {
  benefits: Map<string, Benefit>
  posters: Array<{
    src: string
    alt: string
  }>
}

export const useBenefitsStore = defineStore('benefits', {
  state: (): BenefitsStoreState => ({
    benefits: new Map<string, Benefit>(),
    posters: [],
  }),

  getters: {
    benefitsArray(): Array<Benefit> {
      return Array.from(this.benefits.values())
    },
  },

  actions: {
    async fetchBenefits(): Promise<Array<Benefit>> {
      if (this.benefits.size) {
        return this.benefitsArray
      }

      try {
        await fetcher.get<Benefit>('/data/benefits.json', false, {
          key: 'code',
          map: this.benefits,
        })
      } catch (error: any) {
        // TODO: Add Datadog custom log
      }

      return this.benefitsArray
    },
    benefitsByCategory(category: TBenefitsCategoryValue): Array<Benefit> {
      return this.benefitsArray.filter(
        (benefit: Benefit) => benefit.category === category
      )
    },
    benefitPlans(benefit: Benefit): Array<Plan> {
      const plansStore = usePlansStore()
      const userStore = useUserStore()
      const userPeriod = userStore.highestPlanPeriod

      return benefit.plans
        .map((planType: string) =>
          plansStore.getPlanByTier(planType, userPeriod || undefined)
        )
        .filter((p): p is Plan => isPlan(p))
    },

    /**
     * Retreives all benefits marked with a given redemption category.
     * @param category The redemption category to filter by
     */
    getBenefitsByRedemptionCategory(category: string): Array<Benefit> {
      return this.benefitsArray.filter(
        (benefit) => benefit.redemption_category === category
      )
    },

    /**
     * Checks to see if a specific benefit has been claimed by the current user.
     * @param benefit The benefit to check
     */
    hasClaimedBenefit(benefit: Benefit): boolean {
      const userStore = useUserStore()
      return (
        userStore.claimedCodes.some((claimed) => {
          return (
            claimed.type === benefit.redemption_category &&
            (claimed.subtype ?? null) ===
              (benefit.redemption_subcategory ?? null)
          )
        }) ?? false
      )
    },

    /**
     * Determines if a benefit can be claimed by the current user. Returns
     * `false` if the user has already claimed a code of the same `type` and
     * `subtype`.
     * @param benefit The benefit to check
     */
    canClaimBenefit(benefit: Benefit): boolean {
      const userStore = useUserStore()

      if (userStore.isInTrial) {
        return false
      }

      // If the benefit isn't available in the current plan the bail out early
      if (!this.isAvailableInPlan(benefit)) {
        return false
      }

      // Check for movies and tickets - these are mutually exclusive so if the
      // user has claimed one, the other must also be unclaimable.
      const isMoviesOrTickets = ['movies', 'tickets'].includes(
        benefit.redemption_category
      )

      if (isMoviesOrTickets) {
        const claimedTypes = userStore.claimedCodes.map((c) => c.type)
        const hasClaimedAny = claimedTypes.some((type) =>
          ['movies', 'tickets'].includes(type)
        )
        return !hasClaimedAny
      }

      const categoryClaimed = userStore.claimedCodes
        .filter(
          (claimedCode: ClaimedCode) =>
            claimedCode.type === benefit.redemption_category
        )
        .map((claimedCode: ClaimedCode) => claimedCode.subtype)

      return categoryClaimed.length === 0
    },
    isAvailableInPlan(benefit: Benefit): boolean {
      const userStore = useUserStore()

      if (!userStore.highestPlanTier) {
        return false
      }

      if (!benefit.plans.length) {
        return true
      }

      return benefit.plans.some((planType: TPlanTier) => {
        return (
          userStore.highestPlanTier &&
          isAvailableInPlan(userStore.highestPlanTier, planType)
        )
      })
    },
    async redeem(type: string, subtype?: string): Promise<Array<ClaimedCode>> {
      const userStore = useUserStore()

      if (!userStore.user?.id) {
        throw Error('User must be logged in')
      }

      const user = userStore.user

      const { data, error } = await fetcher.post<{
        csi_content: {
          data?: Array<ClaimedCode>
          code?: string // Tastecard only
          status: number | string
        }
      }>(
        toPortalUrl(`accounts/redeem/${type}/${subtype ?? ''}`),
        {
          email: user.email,
          subtype: subtype ?? '',
          end_trial: userStore.isInTrial,
        },
        true
      )

      if (
        error.value ||
        !data.value ||
        data.value?.csi_content.status === 'error-no-subscription'
      ) {
        throw new Error(
          error.value?.message ??
            `Unable to redeem a ${type} code, please contact support.`
        )
      }

      let fetchedClaimedCodes: Array<ClaimedCode> = []

      // Need to handle tastecard differently as it returns a single, string code
      if (data.value.csi_content.data) {
        const redeemedCodes = data.value.csi_content.data as any

        if (type === BenefitCodes.Tastecard) {
          // TODO Have BE return Tastecard in an array & fix type
          fetchedClaimedCodes = [
            Object.assign(redeemedCodes, {
              claimed_at: DateTime.now(),
              customer_id: user.id,
              type: BenefitCodes.Tastecard,
              subtype: null,
            }),
          ]
        } else if (['movies', 'tickets'].includes(type)) {
          fetchedClaimedCodes = arrayWrap(redeemedCodes).map(
            (code: ClaimedCode) => {
              return Object.assign(code, {
                customer_id: user.id,
                type,
                subtype: null,
              })
            }
          )
        } else {
          fetchedClaimedCodes = arrayWrap(redeemedCodes)
        }
      }

      return userStore.setClaimedCodes(fetchedClaimedCodes)
    },
    async fetchPosters() {
      this.posters = [
        {
          src: '/assets/images/benefits/posters/housemaid.jpg',
          alt: 'The Housemaid',
        },
        {
          src: '/assets/images/benefits/posters/28-years-later.jpg',
          alt: '28 Years Later',
        },
        {
          src: '/assets/images/benefits/posters/hamnet.jpg',
          alt: 'Hamnet',
        },
        {
          src: '/assets/images/benefits/posters/iron-lung.jpg',
          alt: 'Iron Lung',
        },
        {
          src: '/assets/images/benefits/posters/send-help.jpg',
          alt: 'Send Help',
        },
      ]
      // const { data, error } = await fetcher.get(
      //   toWordpressUrl('/movie-posters')
      // )

      // this.posters = data.value
    },
  },
})
